/*
 * Account Table
 *
 * Features:
 * - Renders schema-driven team and RLP account grids with filtering and export data
 * - Reuses the shared team-account server cache across remounts
 *
 * Changes:
 * - 2026-08-26: Keeps counts/export aligned with the current text and column-filter intersection
 * - 2026-08-26: Added explicit team/search empty states
 * - 2026-08-25: Maps normalized company IDs into the legacy RLP Account table column
 * - 2026-08-18: Remounts the data grid when the selected schema changes so row navigation uses the current dropdown
 * - 2026-09-04: Opens RLP rows in the permission-aware paired Account Overview
 * - 2026-08-06: Replaced component-owned account fetching with TanStack Query
 */

import { FC, useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Account } from '../../models/Account';
import Grid from '../common/Grid';
import { type Schema } from '../../context/SchemaContext';
import { type FormField } from '../common/FormField';
import { schemaFieldsToColumnDefs } from '../../utils/columnUtils';
import { createNameCellRenderer } from '../../utils/gridCellRenderers';
import { ColDef, SortDirection, ValueGetterParams } from 'ag-grid-community';
import Spinner from '../common/Spinner';
import { useTeamAccountsQuery } from '../../query/accountQueries';
import { THEME_CLASSES } from '../../constants/themeConstants';
import { Button } from '../primitives';

const RLP_TEAM_ID = -1; // Special teamId value for RLP legacy records

interface AccountTableProps {
  searchQuery: string;
  schema: Schema | null;
  onRowCountChange: (count: number) => void;
  onDataChange?: (data: Account[], columns: Array<{field: string, headerName: string}>) => void;
  onClearSearch: () => void;
  onCreateAccount: () => void;
}

const AccountTable: FC<AccountTableProps> = ({
  searchQuery,
  schema,
  onRowCountChange,
  onDataChange,
  onClearSearch,
  onCreateAccount,
}) => {
  const navigate = useNavigate();

  // Component loaded - simplified export approach

  const [agGridFilteredResult, setAgGridFilteredResult] = useState<{
    sourceData: Account[];
    rows: Account[];
  } | null>(null);

  const { schemaFields, schemaError } = useMemo(() => {
    if (!schema) return { schemaFields: [] as FormField[], schemaError: null as string | null };
    try {
      const fields: FormField[] = JSON.parse(schema.schema);
      return { schemaFields: fields, schemaError: null as string | null };
    } catch (error) {
      console.error('Error parsing schema:', error);
      return {
        schemaFields: [] as FormField[],
        schemaError: 'Invalid schema configuration. Please contact support.',
      };
    }
  }, [schema]);

  const teamId = schema && !schemaError ? (schema.rlp ? RLP_TEAM_ID : schema.teamId) : null;
  const accountsQuery = useTeamAccountsQuery(teamId);
  const accounts = accountsQuery.data ?? [];
  const tableAccounts = useMemo(() => {
    const isRlpAccountSelection = schema?.name.trim().toLowerCase() === 'rlp account';
    if (!isRlpAccountSelection) return accounts;

    return accounts.map((account) => ({
      ...account,
      companyID: account.companyID ?? account.companyId ?? account.rlpCompanyId,
    }));
  }, [accounts, schema]);
  const loading = Boolean(schema && !schemaError && accountsQuery.isPending);
  const error = schemaError
    ?? (accountsQuery.error instanceof Error ? accountsQuery.error.message : accountsQuery.error ? 'Failed to load accounts' : null);

  // No complex AG-Grid API integration needed

  // Simplified grid options without event handlers (Grid component overrides them)
  const gridOptions = useMemo(() => ({
    onRowClicked: (params: { data: Account }) => {
      navigate(`/accounts/${params.data.id}/notes`);
    }
  }), [navigate]);

  // Memoize name cell renderer separately to avoid column regeneration on search
  const nameRenderer = useMemo(() =>
    createNameCellRenderer(searchQuery),
    [searchQuery]
  );

  // Generate column definitions dynamically from schema fields
  const columnDefs = useMemo(() => {
    // Base columns that are always present
    const baseColumns: ColDef<Account>[] = [
      {
        headerName: '',
        width: 50,
        maxWidth: 50,
        minWidth: 50,
        pinned: 'left' as const,
        cellStyle: { borderRight: '1px solid #ddd' },
        valueGetter: (params: ValueGetterParams<Account>) => {
          return (params.node?.rowIndex ?? 0) + 1;
        },
        colId: 'rowNumber',
        sortable: false,
        filter: false
      },
      {
        field: 'name',
        headerName: 'Name',
        sortable: true,
        width: 400,
        pinned: true,
        cellRenderer: nameRenderer
      },
      {
        field: 'noteCount',
        headerName: 'Note Count',
        sortable: true,
        sort: 'desc' as SortDirection,
        type: 'number'
      }
    ];

    // Filter out name and noteCount from schema fields to avoid duplicates
    const filteredSchemaFields = schemaFields.filter(
      field => field.name !== 'name' && field.name !== 'noteCount'
    );

    // Add schema-based columns
    const schemaColumns = schemaFieldsToColumnDefs(filteredSchemaFields);

    return [...baseColumns, ...schemaColumns];
  }, [schemaFields, nameRenderer]);

  // Filter accounts based on search (text filtering only, team filtering done server-side)
  const filteredAccounts = useMemo(() => {
    if (!searchQuery) return tableAccounts;

    const query = searchQuery.toLowerCase();
    return tableAccounts.filter(account => {
      // Check all visible fields for search match
      return schemaFields.some((field: FormField) => {
        if (!(field.name in account)) return false;
        const value = account[field.name as keyof Account];
        return value && String(value).toLowerCase().includes(query);
      }) || account.name?.toLowerCase().includes(query);
    });
  }, [tableAccounts, searchQuery, schemaFields]);

  // Callback handler for AG-Grid filtered data changes
  const handleFilteredDataChange = useCallback((filteredData: Account[]) => {
    setAgGridFilteredResult({ sourceData: filteredAccounts, rows: filteredData });
  }, [filteredAccounts]);

  // Use the grid result only when it was reported for the current source rows. This
  // preserves empty filtered results without reusing a result from an older search/schema.
  useEffect(() => {
    const currentGridFilteredData = agGridFilteredResult?.sourceData === filteredAccounts
      ? agGridFilteredResult.rows
      : null;
    const exportData = currentGridFilteredData ?? filteredAccounts;

    console.log('📊 Export data updated:', {
      columnCount: columnDefs.length,
      loading,
      agGridFiltered: currentGridFilteredData?.length ?? null,
      searchFiltered: filteredAccounts.length,
      usingData: exportData.length
    }); // Enhanced data management log

    if (!loading && columnDefs.length > 0) {
      console.log('✅ Providing export data:', exportData.length, 'accounts',
                  currentGridFilteredData !== null ? '(AG-Grid filtered)' : '(search filtered)'); // Export data log

      // Always update row count with current filtered data
      onRowCountChange(exportData.length);

      // Provide export data
      if (onDataChange) {
        const exportColumns = columnDefs
          .filter(col => col.field && col.field !== 'rowNumber')
          .map(col => ({
            field: col.field!,
            headerName: col.headerName || col.field!
          }));
        onDataChange(exportData, exportColumns);
      }
    }
  }, [filteredAccounts, agGridFilteredResult, columnDefs, onDataChange, onRowCountChange, loading]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-center h-64 flex items-center justify-center">
        {error}
      </div>
    );
  }

  if (filteredAccounts.length === 0) {
    return (
      <div className={`flex h-64 flex-col items-center justify-center gap-3 px-6 text-center ${THEME_CLASSES.text.secondary}`}>
        <h2 className={`text-base font-semibold ${THEME_CLASSES.text.primary}`}>
          {searchQuery ? 'No accounts match your search' : `No accounts in ${schema?.name ?? 'this team'}`}
        </h2>
        <p className="max-w-md text-sm">
          {searchQuery
            ? 'Try another term or clear the current search.'
            : 'Create an account to start building this team list.'}
        </p>
        <Button variant="secondary" size="sm" onClick={searchQuery ? onClearSearch : onCreateAccount}>
          {searchQuery ? 'Clear search' : 'New account'}
        </Button>
      </div>
    );
  }

  return (
    <div className='h-[calc(100vh-150px)]'>
      <Grid
        key={`accounts-${schema?.id || 'default'}`}
        name={`accounts-${schema?.id || 'default'}`}
        data={filteredAccounts}
        columnDefs={columnDefs}
        searchQuery={searchQuery}
        gridOptions={gridOptions}
        columnSelectorPortalTargetId="grid-column-selector-portal"
        onFilteredDataChange={handleFilteredDataChange}
      />
    </div>
  );
}

export default AccountTable;
